import { Pencil } from "lucide-react";
import { useEditModeContext } from "@/contexts/EditModeContext";

// Header/navigation/footer carry real links the admin still needs to
// click to move between pages while Edit Mode is on — a full-area
// overlay would swallow every one of those clicks. These get a small,
// always-visible corner badge instead of the full click-catcher below.
const NAV_MODULE_TYPES = new Set(["header", "navigation", "footer"]);

/**
 * Wraps a rendered module with a click-to-edit overlay while Edit Mode is
 * on. Invisible until hover (a soft tint + "Edit {type}" pill), but always
 * captures the click — entering edit mode trades normal page interaction
 * for "click anything to edit it", same as toggling it off restores
 * normal browsing.
 */
export default function EditableModuleOverlay({ instanceId, moduleType, children }) {
  const { editMode, openEditor } = useEditModeContext();
  if (!editMode || !instanceId) return children;

  if (NAV_MODULE_TYPES.has(moduleType)) {
    return (
      <div className="relative" data-testid={`editable-module-${instanceId}`}>
        {children}
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); openEditor(instanceId); }}
          className="absolute top-2 right-2 z-40 bg-amber-500 text-white text-xs font-semibold px-2.5 py-1 rounded-full shadow-lg flex items-center gap-1.5 hover:bg-amber-600 transition-colors"
          title={`Edit ${moduleType || "module"}`}
          data-testid={`edit-module-btn-${instanceId}`}
        >
          <Pencil className="h-3 w-3" /> Edit {moduleType}
        </button>
      </div>
    );
  }

  return (
    <div className="relative group/slh-editable" data-testid={`editable-module-${instanceId}`}>
      {children}
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); openEditor(instanceId); }}
        className="absolute inset-0 z-40 flex items-center justify-center bg-amber-400/0 group-hover/slh-editable:bg-amber-400/20 outline outline-2 outline-transparent group-hover/slh-editable:outline-amber-400 transition-colors cursor-pointer"
        title={`Edit ${moduleType || "module"}`}
        data-testid={`edit-module-btn-${instanceId}`}
      >
        <span className="opacity-0 group-hover/slh-editable:opacity-100 transition-opacity bg-amber-500 text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1.5 pointer-events-none">
          <Pencil className="h-3.5 w-3.5" /> Edit {moduleType}
        </span>
      </button>
    </div>
  );
}
